/**
 * Checkers multiplayer room.
 *
 * Server is canonical source of truth — sends:
 *   board[8][8] (with values 'b' | 'B' | 'r' | 'R' | null), turn,
 *   last_path, history, winner, end_reason, seats, counts, legal_from
 *
 * Click flow:
 *   - Click your own piece → engine highlights candidate target squares
 *     (single-jump expansions only; multi-jumps are computed locally
 *     by walking server-supplied legal moves).
 *   - Click a highlighted target → either commits a slide (path of
 *     length 2) or starts/continues a multi-jump (path lengthens).
 *   - When the chosen path can no longer extend, it's submitted to
 *     the server.
 *
 * The frontend re-derives candidate paths from the server's board for
 * the seated player only.  This keeps the rules engine in one place
 * (server) but lets the UI feel responsive without a round-trip per
 * intermediate jump-step.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy } from "lucide-react";
import { wsBase } from "@/lib/api";
import { playWoodPlace } from "@/lib/sounds";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { useEerieBackdrop } from "@/lib/eerie";
import CheckersBoard3D from "@/components/Checkers/CheckersBoard3D";

const SIZE = 8;
const PAL = {
  bg:     "radial-gradient(ellipse 70% 50% at 50% 30%, #5A3520 0%, #3B2412 65%, #2A1A0C 100%)",
  panel:  "rgba(40,24,12,0.85)",
  border: "#C99450",
  accent: "#E8B86A",
  ink:    "#F1DFB6",
  muted:  "#B89A75",
  light:  "#E8C99B", // light squares (not used for play but rendered)
  dark:   "#3B2412", // dark squares (gameplay)
  hi:     "rgba(255,224,150,0.65)", // selected/last-move
  legal:  "rgba(255,224,150,0.85)", // legal-target dot
  black:  "#1A0E08",
  red:    "#A52E1A",
};
const TITLE_FONT = "'Textura Libera','UnifrakturMaguntia','Pirata One',serif";
const BODY_FONT  = "'Inter','Helvetica Neue',sans-serif";

// ---------- Local rules helpers (read-only — server validates) ----------
const KING = (p) => p === "B" || p === "R";
const COLOR = (p) => (p === "b" || p === "B" ? "b" : p === "r" || p === "R" ? "r" : null);
const KING_ROW = (color) => (color === "b" ? 7 : 0);
const isDark = (r, c) => 0 <= r && r < SIZE && 0 <= c && c < SIZE && (r + c) % 2 === 1;
const moveDirs = (p) => {
  if (p === "b") return [[1, -1], [1, 1]];
  if (p === "r") return [[-1, -1], [-1, 1]];
  return [[-1, -1], [-1, 1], [1, -1], [1, 1]];
};

function jumpPathsFrom(board, sr, sc) {
  const piece = board[sr][sc];
  if (!piece) return [];
  const color = COLOR(piece);
  const out = [];
  const dfs = (r, c, currentPiece, removed, path) => {
    let any = false;
    for (const [dr, dc] of moveDirs(currentPiece)) {
      const mr = r + dr, mc = c + dc;
      const lr = r + 2 * dr, lc = c + 2 * dc;
      if (!isDark(lr, lc)) continue;
      const mid = board[mr][mc];
      const midColor = COLOR(mid);
      if (!midColor || midColor === color) continue;
      if (removed.has(`${mr},${mc}`)) continue;
      const land = board[lr][lc];
      if (land !== null && !(lr === sr && lc === sc)) continue;
      const promoted = !KING(currentPiece) && lr === KING_ROW(color);
      const newPiece = promoted ? currentPiece.toUpperCase() : currentPiece;
      const newPath = [...path, [lr, lc]];
      const newRemoved = new Set(removed); newRemoved.add(`${mr},${mc}`);
      out.push(newPath);
      any = true;
      if (!promoted) dfs(lr, lc, newPiece, newRemoved, newPath);
    }
    return any;
  };
  dfs(sr, sc, piece, new Set(), [[sr, sc]]);
  return out;
}

function allJumps(board, color) {
  const all = [];
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) {
    if (COLOR(board[r][c]) === color) all.push(...jumpPathsFrom(board, r, c));
  }
  // Keep maximal-prefix paths only.
  const byStart = {};
  for (const p of all) {
    const k = `${p[0][0]},${p[0][1]}`;
    (byStart[k] ||= []).push(p);
  }
  const keep = [];
  for (const k of Object.keys(byStart)) {
    const grp = byStart[k];
    for (const p of grp) {
      const isPrefix = grp.some((q) =>
        q !== p && q.length > p.length &&
        q.slice(0, p.length).every((s, i) => s[0] === p[i][0] && s[1] === p[i][1])
      );
      if (!isPrefix) keep.push(p);
    }
  }
  return keep;
}

function legalMoves(board, color) {
  const jumps = allJumps(board, color);
  if (jumps.length) return jumps;
  // Slides
  const out = [];
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) {
    const piece = board[r][c];
    if (COLOR(piece) !== color) continue;
    for (const [dr, dc] of moveDirs(piece)) {
      const nr = r + dr, nc = c + dc;
      if (isDark(nr, nc) && board[nr][nc] === null) out.push([[r, c], [nr, nc]]);
    }
  }
  return out;
}

// ---------- Piece glyph ----------
function Piece({ piece, size }) {
  const isBlack = piece === "b" || piece === "B";
  const base = isBlack ? PAL.black : PAL.red;
  const inner = isBlack ? "#3D2A1F" : "#D26A4F";
  return (
    <svg width={size * 0.78} height={size * 0.78} viewBox="0 0 100 100" style={{ pointerEvents: "none" }}>
      <circle cx="50" cy="55" r="42" fill="rgba(0,0,0,0.45)" />
      <circle cx="50" cy="50" r="42" fill={base} stroke={PAL.accent} strokeWidth="2.5" />
      <circle cx="50" cy="50" r="32" fill="none" stroke={inner} strokeWidth="2" strokeDasharray="3 3" />
      <circle cx="38" cy="38" r="6" fill="rgba(255,255,255,0.18)" />
      {KING(piece) && (
        <g>
          <path d="M30 60 L36 38 L46 56 L50 30 L54 56 L64 38 L70 60 Z"
            fill={PAL.accent} stroke="rgba(0,0,0,0.5)" strokeWidth="1" />
          <rect x="30" y="62" width="40" height="6" fill={PAL.accent} stroke="rgba(0,0,0,0.5)" strokeWidth="1" />
        </g>
      )}
    </svg>
  );
}

// ---------- Square ----------
function Square({ r, c, piece, isLight, isSelected, isPathStep, isLegalTarget, isLastMove, onClick, size }) {
  const bg = isLight ? PAL.light : PAL.dark;
  return (
    <div onClick={onClick} data-testid={`checkers-sq-${r}-${c}`}
      style={{
        width: size, height: size, position: "relative", overflow: "hidden",
        background: bg,
        cursor: piece || isLegalTarget ? "pointer" : "default",
        boxShadow: isSelected ? `inset 0 0 0 3px ${PAL.hi}` :
                   isPathStep ? `inset 0 0 0 2px ${PAL.legal}` :
                   isLastMove ? `inset 0 0 0 2px ${PAL.hi}` : "none",
      }}>
      {piece && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Piece piece={piece} size={size} />
        </div>
      )}
      {isLegalTarget && !piece && (
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          width: size * 0.3, height: size * 0.3,
          transform: "translate(-50%, -50%)", borderRadius: "50%",
          background: PAL.legal, opacity: 0.6, pointerEvents: "none",
        }} />
      )}
    </div>
  );
}

export default function CheckersRoom() {
  useEerieBackdrop("estampie");
  const navigate = useNavigate();
  const { key } = useParams();
  const wsRef = useRef(null);
  const [state, setState] = useState(null);
  const [you, setYou] = useState(null);
  const [yourName, setYourName] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [boardSize, setBoardSize] = useState(64);
  const [pickPath, setPickPath] = useState(null);  // current click-path {start,steps}

  const myName = useMemo(
    () => (localStorage.getItem("checkers:name") || "Stranger").slice(0, 30),
    []
  );

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setBoardSize(w <= 640 ? 36 : w <= 900 ? 44 : 64);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!key) return;
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let cancelled = false;
    let attempt = 0;
    let lastMoveNum = null;
    const connect = () => {
      if (cancelled) return;
      const url = `${wsBase()}/checkers/ws/${encodeURIComponent(key.toUpperCase())}`;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        attempt = 0;
        ws.send(JSON.stringify({ type: "join", name: myName }));
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          try { if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" })); } catch { /* */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "state") {
            // Knock-on-wood whenever the move number advances —
            // including the very first move (was previously suppressed
            // by `lastMoveNum && ...` which read 0 as falsy).  Per
            // user iter 55: every checker piece move now produces a
            // clear knocking sound, local or remote.
            const mn = msg.move_num || 0;
            if (lastMoveNum !== null && mn > lastMoveNum) {
              try { playWoodPlace(); } catch { /* */ }
            }
            lastMoveNum = mn;
            setState(msg);
            setYou(msg.you);
            setYourName(msg.your_name);
            setPickPath(null);
          } else if (msg.type === "error") {
            setError(msg.error || "An error occurred.");
            if (msg.error !== "room_not_found") setTimeout(() => setError(null), 2200);
          }
        } catch { /* */ }
      };
      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        attempt += 1;
        const wait = Math.min(5000, 400 * attempt);
        reconnectTimer = setTimeout(connect, wait);
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      try { ws && ws.close(); } catch { /* */ }
    };
  }, [key, myName]);

  const board = state?.board || Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  const turn = state?.turn || "b";
  const myTurn = (you === "b" || you === "r") && turn === you && !state?.winner;
  const lastPath = state?.last_path || null;
  const lastSet = useMemo(() => {
    const s = new Set();
    if (lastPath) for (const [r, c] of lastPath) s.add(`${r},${c}`);
    return s;
  }, [lastPath]);

  // Compute the legal-move tree for the current player (re-derived locally
  // from the canonical server board).
  const legal = useMemo(() => (myTurn ? legalMoves(board, you) : []), [board, you, myTurn]);

  // Determine, given an in-progress pickPath, which squares the player
  // can next click — and whether continuing is required (for jumps).
  const targets = useMemo(() => {
    if (!myTurn) return [];
    if (!pickPath) {
      // Origin squares: any square with one of our pieces that has at
      // least one legal move starting from there.
      const out = [];
      const starts = new Set(legal.map((p) => `${p[0][0]},${p[0][1]}`));
      for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) {
        if (starts.has(`${r},${c}`)) out.push([r, c]);
      }
      return out;
    }
    const cur = pickPath.steps;
    // Continuations: legal paths that begin with `cur` exactly.
    const matches = legal.filter((p) =>
      p.length > cur.length &&
      cur.every((s, i) => s[0] === p[i][0] && s[1] === p[i][1])
    );
    const next = matches.map((p) => p[cur.length]);
    return next;
  }, [myTurn, legal, pickPath]);

  const onClick = (r, c) => {
    if (!myTurn) return;
    // Starting a new pick — must click a piece that has legal moves.
    if (!pickPath) {
      const isOrigin = targets.some(([rr, cc]) => rr === r && cc === c);
      if (!isOrigin) return;
      setPickPath({ steps: [[r, c]] });
      return;
    }
    // Cancel by clicking the current head again.
    const head = pickPath.steps[pickPath.steps.length - 1];
    if (head[0] === r && head[1] === c) {
      setPickPath(null);
      return;
    }
    // Allowed next square?
    const okTarget = targets.some(([rr, cc]) => rr === r && cc === c);
    if (!okTarget) {
      // If they clicked a different one of their pieces, restart from there.
      const isFreshOrigin = legal.some((p) => p[0][0] === r && p[0][1] === c);
      if (isFreshOrigin) setPickPath({ steps: [[r, c]] });
      return;
    }
    const newSteps = [...pickPath.steps, [r, c]];
    // If the path is now a complete legal move (no extension exists),
    // submit it.  Otherwise let the player keep jumping.
    const canExtend = legal.some((p) =>
      p.length > newSteps.length &&
      newSteps.every((s, i) => s[0] === p[i][0] && s[1] === p[i][1])
    );
    const isComplete = legal.some((p) =>
      p.length === newSteps.length &&
      newSteps.every((s, i) => s[0] === p[i][0] && s[1] === p[i][1])
    );
    if (isComplete && !canExtend) {
      // Send & clear
      try { wsRef.current?.send(JSON.stringify({ type: "move", path: newSteps })); }
      catch { /* */ }
      setPickPath(null);
    } else {
      setPickPath({ steps: newSteps });
    }
  };

  const resign = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (you !== "b" && you !== "r") return;
    if (state?.winner) return;
    if (!window.confirm("Resign this match?")) return;
    wsRef.current.send(JSON.stringify({ type: "resign" }));
  };
  const newGame = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (!state?.winner) return;
    wsRef.current.send(JSON.stringify({ type: "new_game" }));
  };
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(key.toUpperCase());
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 1500);
    } catch { /* */ }
  };

  // Orientation: BOTH players see their own pieces at the bottom of
  // their screen (per user spec).  Black's view is now also flipped
  // so the host's pieces sit in the bottom half — matching the red
  // player's experience.
  const flipped = you === "r" || you === "b";
  const rows = flipped ? Array.from({ length: SIZE }, (_, i) => SIZE - 1 - i) : Array.from({ length: SIZE }, (_, i) => i);
  const cols = flipped ? Array.from({ length: SIZE }, (_, i) => SIZE - 1 - i) : Array.from({ length: SIZE }, (_, i) => i);

  // Status
  let statusLine = "Connecting…";
  let emphasise = false;
  if (error === "room_not_found") {
    statusLine = "Match not found. Return to the lobby."; emphasise = true;
  } else if (!connected) statusLine = "Connecting…";
  else if (!state) statusLine = "Joining match…";
  else if (state.winner) {
    const winSide = state.winner === "b" ? "Black" : "Red";
    const myWin = (you === "b" || you === "r") && state.winner === you;
    const reason = state.end_reason === "resign" ? "by resignation"
                 : state.end_reason === "no_legal_moves" ? "— opponent has no legal moves"
                 : "";
    statusLine = `${winSide} wins ${reason}.${myWin ? "  (You win!)" : ""}`;
    emphasise = true;
  } else {
    const seatedB = !!state.seats?.b;
    const seatedR = !!state.seats?.r;
    if (!seatedB || !seatedR) {
      statusLine = "Waiting for opponent… Share the match-key.";
    } else {
      const turnSide = turn === "b" ? "Black" : "Red";
      const turnName = state.seats?.[turn] || turnSide;
      statusLine = turn === you
        ? `Your turn (${turnSide}).${pickPath ? "  Continue jumping or land." : ""}`
        : `${turnName} (${turnSide}) is to move.`;
    }
  }
  const myColourLabel = you === "b" ? "Black" : you === "r" ? "Red" : "Spectator";

  // Pre-compute path step set + legal-target set for rendering
  const pathSet = useMemo(() => {
    const s = new Set();
    if (pickPath) for (const [r, c] of pickPath.steps) s.add(`${r},${c}`);
    return s;
  }, [pickPath]);
  const targetSet = useMemo(() => {
    const s = new Set();
    for (const [r, c] of targets) s.add(`${r},${c}`);
    return s;
  }, [targets]);
  const selectedSq = pickPath ? pickPath.steps[pickPath.steps.length - 1] : null;

  return (
    <div className="relative min-h-screen overflow-x-clip"
      data-testid="checkers-room-page"
      style={{ background: PAL.bg, color: PAL.ink, fontFamily: BODY_FONT }}>
      <TopBar theme="studio" onBack={() => navigate("/games/checkers")} backLabel="LOBBY" testId="checkers-room-back-button" />
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r" style={{ borderColor: `${PAL.accent}26` }}>
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 px-6 md:px-12 pt-10 pb-16">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: PAL.accent, fontFamily: "'Cinzel','Playfair Display',serif" }}>
            Multiplayer · Match {key?.toUpperCase()}
          </div>
          <h1 className="mb-2" data-testid="game-title-checkers"
            style={{
              fontFamily: TITLE_FONT, fontWeight: 700, color: PAL.ink,
              fontSize: "clamp(1.6rem, 3vw, 2.4rem)", lineHeight: 1.05,
              letterSpacing: "0.02em",
              textShadow: "0 2px 0 rgba(0,0,0,0.5), 0 0 22px rgba(232,184,106,0.22)",
            }}>
            {state?.name || "Checkers"}
          </h1>
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="text-xs tracking-[0.3em] uppercase" style={{ color: PAL.muted, fontFamily: "'Cinzel','Playfair Display',serif" }}>
              Match-key
            </div>
            <div data-testid="checkers-room-key" style={{
              background: PAL.panel, border: `1px solid ${PAL.border}`,
              padding: "4px 12px", letterSpacing: "0.4em", fontSize: 16,
              fontFamily: "'Cinzel','Playfair Display',serif", color: PAL.ink,
            }}>{key?.toUpperCase()}</div>
            <button onClick={copyKey} data-testid="checkers-copy-key"
              className="text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1"
              style={{ color: PAL.accent, fontFamily: "'Cinzel','Playfair Display',serif" }}>
              <Copy size={12} /> {keyCopied ? "Copied!" : "Copy"}
            </button>
            <div className="flex-1" />
            <div className="text-xs" style={{ color: PAL.muted, fontFamily: "'Cinzel','Playfair Display',serif" }}>
              You: <span style={{ color: PAL.ink }}>{yourName || myName}</span>{" · "}{myColourLabel}
            </div>
          </div>

          <div className="flex flex-col xl:flex-row gap-8">
            <div>
              <div className="flex items-center gap-6 mb-3 text-xs tracking-[0.2em] uppercase"
                style={{ color: PAL.muted, fontFamily: "'Cinzel','Playfair Display',serif" }}>
                <span data-testid="checkers-pieces-black">
                  ● {state?.seats?.b || "Black"}: {(state?.counts?.b ?? 0) + (state?.counts?.B ?? 0)} pieces
                </span>
                <span data-testid="checkers-pieces-red">
                  ● {state?.seats?.r || "Red"}: {(state?.counts?.r ?? 0) + (state?.counts?.R ?? 0)} pieces
                </span>
              </div>

              <CheckersBoard3D
                board={board}
                flipped={flipped}
                selectedSq={selectedSq}
                pathSet={pathSet}
                targetSet={targetSet}
                lastSet={lastSet}
                onSquareClick={onClick}
                onBackdropClick={() => { setPickPath(null); }}
                height={Math.max(420, boardSize * SIZE * 0.85)}
              />

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <div data-testid="checkers-status" className="text-sm"
                  style={{
                    color: emphasise ? PAL.ink : PAL.accent,
                    fontFamily: "'Cinzel','Playfair Display',serif",
                    fontWeight: emphasise ? 700 : 500,
                  }}>
                  {statusLine}
                </div>
                <div className="flex-1" />
                {state?.winner ? (
                  <button onClick={newGame} disabled={you !== "b" && you !== "r"}
                    data-testid="checkers-new-game"
                    className="px-3 py-1.5 text-xs tracking-[0.25em] uppercase"
                    style={{
                      background: PAL.ink, color: "#1F140A",
                      border: `1px solid ${PAL.border}`, fontWeight: 700,
                      fontFamily: "'Cinzel','Playfair Display',serif",
                      opacity: you !== "b" && you !== "r" ? 0.4 : 1,
                    }}>
                    New Game (swap colours)
                  </button>
                ) : (
                  <button onClick={resign} disabled={you !== "b" && you !== "r"}
                    data-testid="checkers-resign"
                    className="px-3 py-1.5 text-xs tracking-[0.25em] uppercase"
                    style={{
                      background: "transparent", color: PAL.accent,
                      border: `1px solid ${PAL.border}`, fontWeight: 600,
                      fontFamily: "'Cinzel','Playfair Display',serif",
                      opacity: you !== "b" && you !== "r" ? 0.4 : 1,
                    }}>
                    Resign
                  </button>
                )}
              </div>
              {error && error !== "room_not_found" && (
                <div className="mt-3 text-xs" style={{ color: "#FF8B8B" }} data-testid="checkers-room-error">
                  {error}
                </div>
              )}
            </div>

            <div className="xl:w-72">
              <div className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: PAL.accent, fontFamily: "'Cinzel','Playfair Display',serif" }}>
                Move History
              </div>
              <div className="overflow-y-auto"
                style={{ background: PAL.panel, border: `1px solid ${PAL.border}`, maxHeight: 540, padding: "10px 12px",
                         fontFamily: "'Cinzel','Playfair Display',serif" }}
                data-testid="checkers-history">
                {!state || state.history.length === 0 ? (
                  <div style={{ color: PAL.muted, fontStyle: "italic", fontSize: 13 }}>
                    No moves yet.  Black opens.
                  </div>
                ) : (
                  <table style={{ width: "100%", fontSize: 12 }}>
                    <tbody>
                      {state.history.map((h) => (
                        <tr key={h.n}>
                          <td style={{ width: 32, color: PAL.muted, padding: "2px 4px" }}>{h.n}.</td>
                          <td style={{ padding: "2px 4px", color: h.color === "b" ? PAL.muted : PAL.ink }}>
                            {h.color === "b" ? "●" : "●"} {h.path.map(([r, c]) => `${String.fromCharCode(97 + c)}${SIZE - r}`).join(h.captures ? "×" : "→")}
                          </td>
                          <td style={{ padding: "2px 4px", color: PAL.accent, fontSize: 11 }}>
                            {h.captures ? `(${h.captures})` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
